import { FC, ReactNode, useMemo, useState } from "react";
import { motion } from "motion/react";
import { MarkdownV1SessionPart, SessionEventKind, SessionFeature } from "../session.types";
import styled from "@emotion/styled";
import { MDSSessionPartContainer } from "@/mds/components/sessions/session-parts/session-part.style";
import {
  SessionPartProps,
  SessionPartContext,
  SessionPartConfig,
} from "@/mds/components/sessions/session-parts/session-part.types";
import { MDSBubble } from "@/mds/components/bubble/bubble";
import { MDSBubbleAlignment, MDSBubbleVariant } from "@/mds/components/bubble/bubble.types";
import {
  MemRoutableItemKind,
  OnClickMemItem,
  OnMemItemOut,
  OnMemItemOver,
  syntaxTreeModule,
} from "@mem-labs/common-editor";
import {
  useIsFirstPartInSessionGroup,
  useIsLastPartInSessionGroup,
  useIsLastUserMessagePartInSessionHistory,
} from "@/mds/components/sessions/session-context/mds-session-context.hooks";
import { MDSSessionSentBadge } from "../session-sent-badge";
import { getSessionPartClientMeta } from "@/mds/components/sessions/session-parts/common/session-part-client-meta";
import { SessionMarkdownContent } from "@/mds/components/sessions/session-parts/common/session-markdown-content";
import { MDSSessionThreadRow } from "@/mds/components/sessions/session-parts/common/session-thread-row";
import { AttachmentReference } from "@/mds/components/sessions/session-parts/common/attachment-reference-utils";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { MDSSessionFeedback } from "../session-feedback";
import { MDSSessionContextContainer } from "@/mds/components/sessions/session-parts/session-context-container";
import { MDSIconButton, MDSIconButtonVariant } from "@/mds/components/buttons";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { MDSIcon, MDSIconKind } from "@/mds/components/icon";

type Props = SessionPartProps<MarkdownV1SessionPart>;

const COPY_FEEDBACK_DURATION_MS = 1000;
const USER_MESSAGE_ACTION_HOVER_DELAY_MS = 100;

export const MDSSessionMarkdownV1Part: FC<Props> = ({ part, context, config }) => {
  const isFirstInGroup = useIsFirstPartInSessionGroup({ part });
  const isLastInGroup = useIsLastPartInSessionGroup({ part });
  const isLastUserMessage = useIsLastUserMessagePartInSessionHistory({ part });
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const WrapperComponent: FC<IWrapperComponentProps> = (() => {
    if (
      context.eventKind === SessionEventKind.AgentActionV1 ||
      context.eventKind === SessionEventKind.AgentPlanningV1 ||
      context.eventKind === SessionEventKind.SystemNotificationV1
    ) {
      return AgentActionWrapper;
    }

    return StandardWrapper;
  })();

  const onClickHandler: OnClickMemItem = (kind, id, target, event) => {
    if (!config.linkHandler) {
      return;
    }

    let mentionKind: MemCommonMentionKind | null = null;
    switch (kind) {
      case MemRoutableItemKind.NOTES:
        mentionKind = MemCommonMentionKind.Note;
        break;
      case MemRoutableItemKind.COLLECTIONS:
        mentionKind = MemCommonMentionKind.Collection;
        break;
      case MemRoutableItemKind["TIME-RANGES"]:
        mentionKind = MemCommonMentionKind.TimeRange;
        break;
      default:
        mentionKind = null;
        break;
    }

    config.linkHandler(mentionKind, id, target, event);
  };

  const handleMemItemOver: OnMemItemOver = (kind, itemId, target) => {
    if (!config.onMemItemOver) {
      return;
    }

    let mentionKind: MemCommonMentionKind | null = null;
    switch (kind) {
      case MemRoutableItemKind.NOTES:
        mentionKind = MemCommonMentionKind.Note;
        break;
      case MemRoutableItemKind.COLLECTIONS:
        mentionKind = MemCommonMentionKind.Collection;
        break;
      case MemRoutableItemKind["TIME-RANGES"]:
        mentionKind = MemCommonMentionKind.TimeRange;
        break;
      default:
        mentionKind = null;
        break;
    }

    config.onMemItemOver?.(mentionKind, itemId, target);
  };

  const handleMemItemOut: OnMemItemOut = () => {
    config.onMemItemOut?.();
  };

  // Convert MDX to standard Markdown for proper link parsing
  const content = syntaxTreeModule.convertMdxToMarkdown({
    mdxContent: part.content,
  });

  const meta = getSessionPartClientMeta(part);

  const sentBadgeStatus = meta?.status;

  const isVoiceMessage =
    context.eventKind === SessionEventKind.UserActionV1 && meta?.source === "voice";

  const hasSentBadgeStatus = useMemo(() => {
    return typeof sentBadgeStatus === "string";
  }, [sentBadgeStatus]);

  const isMessageSent = useMemo(() => {
    return sentBadgeStatus === "sent";
  }, [sentBadgeStatus]);

  const isSentBadgeRenderable = useMemo(() => {
    if (context.eventKind !== SessionEventKind.UserActionV1) {
      return false;
    }

    return isLastInGroup && isLastUserMessage && hasSentBadgeStatus;
  }, [hasSentBadgeStatus, isLastInGroup, isLastUserMessage, context.eventKind]);

  const isSentBadgeVisible = useMemo(() => {
    if (!isSentBadgeRenderable) {
      return false;
    }

    return isMessageSent;
  }, [isSentBadgeRenderable, isMessageSent]);

  const canCopyMessage = config.features?.includes(SessionFeature.AllowCopy) ?? false;

  // Threads anchor on individual messages, so a threads-enabled session
  // shows the actions row under EVERY message (space is reserved, so hover
  // causes no layout shift); otherwise only the group's last message has it.
  const showActionsForEveryMessage = !!config.threads;

  // Show feedback for agent messages (responses from the AI)
  const shouldShowAgentActions = useMemo(() => {
    return (
      (context.eventKind === SessionEventKind.AgentActionV1 ||
        context.eventKind === SessionEventKind.AgentPlanningV1) &&
      (isLastInGroup || showActionsForEveryMessage)
    );
  }, [context.eventKind, isLastInGroup, showActionsForEveryMessage]);

  const shouldShowUserActions = useMemo(() => {
    return (
      context.eventKind === SessionEventKind.UserActionV1 &&
      (isLastInGroup || showActionsForEveryMessage) &&
      (canCopyMessage || !!config.threads)
    );
  }, [
    canCopyMessage,
    config.threads,
    context.eventKind,
    isLastInGroup,
    showActionsForEveryMessage,
  ]);

  const handleCopyMessage = async () => {
    try {
      const html = syntaxTreeModule.convertMdxToHtml({
        mdxContent: part.content,
      });

      const item = new ClipboardItem({
        ["text/html"]: new Blob([html], { type: "text/html" }),
        ["text/plain"]: new Blob([part.content], { type: "text/plain" }),
      });

      await navigator.clipboard.write([item]);

      setIsCopied(true);
    } catch (error) {
      console.error(error);
    }

    setTimeout(() => setIsCopied(false), COPY_FEEDBACK_DURATION_MS);
  };

  const copyMessageAction = isCopied ? (
    <StyledCheckIconWrapper>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        <MDSIcon kind={MDSIconKind.Check} />
      </motion.div>
    </StyledCheckIconWrapper>
  ) : (
    <MDSTooltip
      config={{
        label: "Copy",
        placement: MDSTooltipPlacement.Top,
      }}
    >
      <MDSIconButton
        aria-label="Copy to clipboard"
        iconKind={MDSIconKind.Copy}
        onClick={handleCopyMessage}
        variant={MDSIconButtonVariant.Transparent}
      />
    </MDSTooltip>
  );

  // The reply-count link is persistent (visible without hover) and REPLACES
  // the start-thread icon in the same row slot — a message shows one thread
  // affordance, never both.
  const threadReplyCount = config.threads ? (context.threadReplyCount ?? 0) : 0;
  const hasThreadReplies = threadReplyCount > 0;
  const threadRepliesLink = hasThreadReplies ? (
    <MDSSessionThreadRow
      replyCount={threadReplyCount}
      onOpenThread={() => config.threads?.onOpenThread(context.eventId)}
      showUserAvatar={context.threadHasUserReply === true}
      showAgentAvatar={context.threadHasAgentReply === true}
      userPhotoUrl={config.threads?.userPhotoUrl}
    />
  ) : null;

  const startThreadAction =
    config.threads && !hasThreadReplies ? (
      <MDSTooltip
        config={{
          label: "Start a thread",
          placement: MDSTooltipPlacement.Top,
        }}
      >
        <MDSIconButton
          aria-label="Start a thread"
          iconKind={MDSIconKind.Comment}
          onClick={() => config.threads?.onStartThread(context.eventId)}
          variant={MDSIconButtonVariant.Transparent}
        />
      </MDSTooltip>
    ) : null;

  return (
    <Wrapper
      data-test-id="session-message-wrapper"
      $hasUserActions={shouldShowUserActions}
      $reserveActionsRow={showActionsForEveryMessage}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <MessageActionAnchor>
        <WrapperComponent
          isFirstInGroup={isFirstInGroup}
          isLastInGroup={isLastInGroup}
          context={context}
          config={config}
        >
          <Container eventKind={context.eventKind} $isVoice={isVoiceMessage}>
            <SessionMarkdownContent
              onClickMemItem={onClickHandler}
              onMemItemOver={handleMemItemOver}
              onMemItemOut={handleMemItemOut}
              config={config}
              content={content}
              renderLinksAsChips={context.eventKind === SessionEventKind.UserActionV1}
              attachmentReferences={
                (part as MarkdownV1SessionPart & { attachment_references?: AttachmentReference[] })
                  .attachment_references
              }
            />
          </Container>
          {shouldShowAgentActions && (
            <ActionsContainer isVisible={isHovered || !!context.feedbackGiven || hasThreadReplies}>
              {/* Link first on the left-aligned agent side: the hidden hover
                  group still takes width, and a trailing link would sit
                  indented when unhovered. */}
              {threadRepliesLink}
              <StyledHoverActions $isVisible={isHovered || !!context.feedbackGiven}>
                {canCopyMessage && copyMessageAction}
                {startThreadAction}
                {config.feedbackHandler && (
                  <MDSSessionFeedback
                    eventId={context.eventId}
                    eventKind={context.eventKind}
                    onFeedback={config.feedbackHandler}
                    feedbackGiven={context.feedbackGiven}
                  />
                )}
              </StyledHoverActions>
            </ActionsContainer>
          )}
        </WrapperComponent>
        {shouldShowUserActions && (
          <UserActionsContainer
            data-test-id="session-message-user-actions"
            isVisible={isHovered || isCopied || hasThreadReplies}
            visibilityDelayMs={USER_MESSAGE_ACTION_HOVER_DELAY_MS}
          >
            <StyledHoverActions $isVisible={isHovered || isCopied}>
              {canCopyMessage && copyMessageAction}
              {startThreadAction}
            </StyledHoverActions>
            {threadRepliesLink}
          </UserActionsContainer>
        )}
      </MessageActionAnchor>
      {isSentBadgeRenderable && <MDSSessionSentBadge isVisible={isSentBadgeVisible} label="Sent" />}
    </Wrapper>
  );
};

interface IWrapperComponentProps {
  children: ReactNode;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  context: SessionPartContext;
  config: SessionPartConfig;
}

const AgentActionWrapper: FC<IWrapperComponentProps> = ({
  children,
  isFirstInGroup,
  isLastInGroup,
  config: _config,
}) => {
  return (
    <Bubble
      variant={MDSBubbleVariant.Transparent}
      align={MDSBubbleAlignment.Left}
      isLastInGroup={isLastInGroup}
    >
      <AgentMarkdownContentWrapper isFirstInGroup={isFirstInGroup}>
        {children}
      </AgentMarkdownContentWrapper>
    </Bubble>
  );
};

const StandardWrapper: FC<IWrapperComponentProps> = ({
  children,
  isLastInGroup,
  context,
  config,
}) => {
  // Extract context mentions for user messages
  const mentions =
    context.eventKind === SessionEventKind.UserActionV1 ? context.mentions : undefined;

  return (
    <Bubble
      variant={MDSBubbleVariant.MessageUser}
      align={MDSBubbleAlignment.Right}
      isLastInGroup={isLastInGroup}
    >
      {mentions && <MDSSessionContextContainer mentions={mentions} config={config} />}
      {children}
    </Bubble>
  );
};

const Bubble = styled(MDSBubble)`
  width: 100%;
`;

const Container = styled(MDSSessionPartContainer)<{
  eventKind: SessionEventKind;
  $isVoice?: boolean;
}>`
  align-items: center;
  margin-bottom: ${({ theme, eventKind }) =>
    eventKind === SessionEventKind.AgentActionV1 || eventKind === SessionEventKind.AgentPlanningV1
      ? theme.spacing["1"]
      : "0"};
  font-style: ${({ $isVoice }) => ($isVoice ? "italic" : "normal")};
`;

const Wrapper = styled.div<{ $hasUserActions: boolean; $reserveActionsRow: boolean }>`
  width: 100%;
  margin-bottom: ${({ $hasUserActions, theme }) => ($hasUserActions ? "0" : theme.spacing["2"])};
  /* The user actions row is absolutely positioned 2px below the bubble and
     is a 32px icon row. With per-message rows (threads), the next grouped
     bubble follows immediately, so the full row height must be reserved;
     spacing[4] is only enough when the row sits on the group's last message
     where the next group's margins absorb the rest. */
  padding-bottom: ${({ $hasUserActions, $reserveActionsRow, theme }) => {
    if (!$hasUserActions) return "0";
    return $reserveActionsRow ? theme.spacing["10"] : theme.spacing["4"];
  }};
`;

const MessageActionAnchor = styled.div`
  position: relative;
  width: 100%;
`;

const AgentMarkdownContentWrapper = styled.div<{ isFirstInGroup: boolean }>`
  margin-top: ${({ isFirstInGroup, theme }) => (isFirstInGroup ? theme.spacing["1"] : "0")};
`;

const ActionsContainer = styled.div<{ isVisible: boolean; visibilityDelayMs?: number }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: ${({ isVisible }) => (isVisible ? "auto" : "none")};
  transition: opacity 0.2s ease-in-out;
  transition-delay: ${({ isVisible, visibilityDelayMs = 0 }) =>
    isVisible ? `${visibilityDelayMs}ms` : "0ms"};
`;

/* Inner hover-gated group: the containing actions row can stay visible for
   the persistent reply link while the icon actions remain hover-only. */
const StyledHoverActions = styled.div<{ $isVisible: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible }) => ($isVisible ? "auto" : "none")};
  transition: opacity 0.2s ease-in-out;
`;

const UserActionsContainer = styled(ActionsContainer)`
  position: absolute;
  right: 0;
  top: calc(100% + ${({ theme }) => theme.spacing["0.5"]});
  z-index: 1;
  justify-content: flex-end;
  width: 100%;
`;

// Matches MDSIconButton Medium size: 12px padding + 14px icon = 38px total
const StyledCheckIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: ${({ theme }) => theme.colors.icon.primary};

  svg {
    width: 14px;
    height: 14px;
  }
`;
